/**
 * MessagesList - Presentation component for displaying chat messages
 * Follows ADR-004: Separation of Concerns - Pure presentation component
 */

import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { WelcomeMessage } from './WelcomeMessage';
import { MessageBubble } from './MessageBubble';
import { formatMessageContent } from '../utils/messageFormat';
import { isAgentSource } from '../utils/agentTurnGrouping';
import type { MessageInterface } from '../hooks/useMessageProcessing';
import type { PendingMessage } from '../hooks/useMessageProcessing';
import type { Language } from '../hooks/useTranslation';
import type { ButtonVariant } from '../types/theme';

/**
 * Props for MessagesList component
 */
interface MessagesListProps {
  /** Array of messages to display */
  messages: MessageInterface[];
  /** Pending message (if any) */
  pendingMessage?: PendingMessage;
  /** Status message (if any) */
  statusMessage?: MessageInterface | null;
  /** Whether session is being created */
  isCreatingSession: boolean;
  /** Whether to show welcome message */
  showWelcomeMessage: boolean;
  /** Agent name for display */
  agentName: string;
  /** Customer name for personalization */
  customerName?: string;
  /** Language for the interface */
  language: Language;
  /** Callback when welcome message appears */
  onWelcomeMessageAppear?: () => void;
  /** Whether welcome was already shown */
  hasShownWelcome: boolean;
  /** Custom welcome messages */
  welcomeMessages?: Record<'da' | 'en', string>;
  /** Function to get display name for message source */
  getSourceDisplayName: (source: string, participantName?: string) => string;
  /** Function to format timestamp */
  formatTimestamp: (timestamp: Date) => string;
  /** Translation function */
  t: (key: string, params?: Record<string, string | number>) => string;
  /** Optional callback when a button in a message is clicked */
  onButtonClick?: (url: string, label: string, variant?: ButtonVariant) => void;
  /** Whether debug mode is enabled (shows draft, logs, etc.) */
  debugMode?: boolean;
  /** Layout mode - affects message bubble width */
  layoutMode?: 'popup' | 'fullscreen';
}

interface StatusBubbleProps {
  statusMessage: MessageInterface;
  getSourceDisplayName: (source: string, participantName?: string) => string;
  formatTimestamp: (timestamp: Date) => string;
  onButtonClick?: (url: string, label: string, variant?: ButtonVariant) => void;
  layoutMode?: 'popup' | 'fullscreen';
}

/**
 * Standalone typing/status pill for uncorrelated server status events.
 */
const StatusBubble: React.FC<StatusBubbleProps> = ({
  statusMessage,
  getSourceDisplayName,
  formatTimestamp,
  onButtonClick,
  layoutMode = 'fullscreen',
}) => {
  return (
    <div className="opacity-0 animate-status-appear">
      <MessageBubble
        key="status-typing-indicator"
        message={statusMessage}
        displayName={getSourceDisplayName(statusMessage.source)}
        timestamp={formatTimestamp(new Date(statusMessage.creationUtc || 0))}
        isStatusMessage={true}
        onButtonClick={onButtonClick}
        layoutMode={layoutMode}
      />
    </div>
  );
};

/**
 * MessagesList - Pure presentation component for rendering chat messages
 * 
 * @example
 * ```tsx
 * <MessagesList
 *   messages={messages}
 *   isCreatingSession={false}
 *   showWelcomeMessage={true}
 *   agentName="Agent"
 *   language="en"
 *   getSourceDisplayName={getSourceDisplayName}
 *   formatTimestamp={formatTimestamp}
 *   t={t}
 * />
 * ```
 */
// eslint-disable-next-line max-lines-per-function
export const MessagesList: React.FC<MessagesListProps> = ({
  messages,
  pendingMessage,
  statusMessage,
  isCreatingSession,
  showWelcomeMessage,
  agentName,
  customerName,
  language,
  onWelcomeMessageAppear,
  hasShownWelcome,
  welcomeMessages,
  getSourceDisplayName,
  formatTimestamp,
  t,
  onButtonClick,
  debugMode = false,
  layoutMode = 'fullscreen',
}) => {
  // Find the index of the last agent message (not customer, not status)
  const lastAgentMessageIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.source !== 'customer' && !msg.isStatusMessage) {
        return i;
      }
    }
    return -1;
  }, [messages]);

  const isContinualMessage = (index: number): boolean => {
    if (index === 0) return false;
    const current = messages[index];
    const previous = messages[index - 1];
    
    // Same source and same correlationId = continual message
    return current.source === previous.source && 
           current.correlationId === previous.correlationId &&
           current.correlationId !== ''; // Don't treat empty correlationIds as continual
  };
  // Standalone status is only used when the server omits a correlation id.
  // Correlated status is rendered inline on the active turn (or placeholder shell).
  const showStandaloneStatusBubble = Boolean(statusMessage);

  const copyableText = useMemo(() => {
    if (lastAgentMessageIndex === -1) return '';

    const textParts: string[] = [];
    
    // Work backwards from the last agent message, collecting agent messages
    // until we hit a customer message
    for (let i = lastAgentMessageIndex; i >= 0; i--) {
      const msg = messages[i];
      if (msg.source === 'customer' || msg.source === 'customer_ui') {
        break; // Stop at the previous user message
      }
      if (!msg.isStatusMessage) {
        const rawMessage = (msg.data as { message?: string | Record<string, unknown> })?.message;
        const messageText = formatMessageContent(rawMessage);
        if (messageText) {
          textParts.unshift(messageText); // Add to front to maintain order
        }
      }
    }

    return textParts.join('\n\n');
  }, [messages, lastAgentMessageIndex]);
  const getMessageKey = (message: MessageInterface | PendingMessage): string => {
    const typedMessage = message as MessageInterface;
    if (isAgentSource(typedMessage.source) && typedMessage.correlationId) {
      return `turn-anchor-${typedMessage.correlationId}`;
    }
    if (message.id) return message.id;
    if (typedMessage.isStatusMessage) return 'status-indicator';
    if (message.serverStatus === 'pending') {
      return `pending-${message.correlationId || 'unknown'}`;
    }
    if (message.correlationId) {
      return `corr-${message.correlationId}-${message.offset}`;
    }
    return `offset-${message.offset}`;
  };

  if (isCreatingSession) {
    return (
      <div className="chatbot-loading flex items-center justify-center h-full">
        <div className="text-center space-y-4">
          <Loader2 size={32} className="chatbot-loading-spinner animate-spin text-primary mx-auto" />
          <p className="chatbot-loading-text text-muted-foreground">{t('status.connecting', { agentName })}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="chatbot-messages-list space-y-3 sm:space-y-4">
      {showWelcomeMessage && (
        <WelcomeMessage
          agentName={agentName}
          customerName={customerName}
          language={language}
          onMessageAppear={onWelcomeMessageAppear}
          delay={1000}
          skipAnimation={hasShownWelcome}
          customWelcomeText={welcomeMessages?.[language]}
          onButtonClick={onButtonClick}
        />
      )}
      {messages.length > 0 &&
        messages.map((message, index) => {
          const messageData = message.data as { message?: string; participant?: { display_name?: string } };
          const displayName = getSourceDisplayName(
            message.source,
            messageData?.participant?.display_name
          );
          const messageDate = new Date(message.created_at || message.creationUtc || 0);
          const timestamp = formatTimestamp(messageDate);
          const isLastAgentMessage = index === lastAgentMessageIndex;
          const isContinual = isContinualMessage(index);

          return (
            <div key={getMessageKey(message)}>
              <div className={isContinual ? 'mt-1' : ''}>
                <MessageBubble
                  message={message}
                  displayName={displayName}
                  timestamp={timestamp}
                  isStatusMessage={message.isStatusMessage}
                  onButtonClick={onButtonClick}
                  isLastAgentMessage={isLastAgentMessage}
                  copyableText={isLastAgentMessage ? copyableText : undefined}
                  copyTooltip={isLastAgentMessage ? t('message.copyTooltip') : undefined}
                  isContinual={isContinual}
                  t={t}
                  debugMode={debugMode}
                  layoutMode={layoutMode}
                />
              </div>
            </div>
          );
        })}
      {(() => {
        const pendingText = pendingMessage?.data?.message;
        if (!pendingText) return null;
        
        // Check if this pending message's content is already in the confirmed messages
        // This prevents the "flash" when transitioning from pending to confirmed
        // Use trimmed comparison to handle whitespace differences
        const pendingTextTrimmed = pendingText.trim();
        const alreadyInMessages = messages.some(msg => {
          if (msg.source !== 'customer' && msg.source !== 'customer_ui') return false;
          const msgText = (msg.data as { message?: string })?.message;
          return msgText?.trim() === pendingTextTrimmed;
        });
        
        if (alreadyInMessages) return null;
        
        return (
          <MessageBubble
            key={`pending-${pendingMessage.correlationId}`}
            message={pendingMessage as MessageInterface}
            displayName={getSourceDisplayName(pendingMessage.source)}
            timestamp={formatTimestamp(new Date(pendingMessage.creationUtc || 0))}
            isStatusMessage={false}
            onButtonClick={onButtonClick}
            layoutMode={layoutMode}
          />
        );
      })()}
      {showStandaloneStatusBubble && statusMessage && (
        <StatusBubble
          statusMessage={statusMessage}
          getSourceDisplayName={getSourceDisplayName}
          formatTimestamp={formatTimestamp}
          onButtonClick={onButtonClick}
          layoutMode={layoutMode}
        />
      )}
    </div>
  );
};

